import DataTable from 'datatables.net-bs5';
import * as Highcharts from 'highcharts';
import { getChartTheme } from './utils/chartTheme';

declare global {
    interface Window {
        perf_data?: PerfChartPayload;
        perf_meta?: { hasData: boolean };
    }
}

type Metric = 'ttfb_ms' | 'fcp_ms' | 'lcp_ms' | 'cls' | 'db_total_ms' | 'bundle_kb';

interface MetricSeries {
    [route: string]: Array<[number, number]>;
}

interface PerfChartPayload {
    routes: string[];
    series: {
        ttfb_ms: MetricSeries;
        fcp_ms: MetricSeries;
        lcp_ms: MetricSeries;
        cls: MetricSeries;
        db_total_ms: MetricSeries;
        bundle_kb: MetricSeries;
        [key: string]: MetricSeries;
    };
}

interface MetricConfig {
    key: Metric;
    elId: string;
    title: string;
    yTitle: string;
    ySuffix: string;
    decimals: number;
}

const METRICS: MetricConfig[] = [
    { key: 'ttfb_ms',     elId: 'perf-chart-ttfb',   title: 'TTFB',        yTitle: 'ms', ySuffix: ' ms', decimals: 0 },
    { key: 'fcp_ms',      elId: 'perf-chart-fcp',    title: 'FCP',         yTitle: 'ms', ySuffix: ' ms', decimals: 0 },
    { key: 'lcp_ms',      elId: 'perf-chart-lcp',    title: 'LCP',         yTitle: 'ms', ySuffix: ' ms', decimals: 0 },
    { key: 'cls',         elId: 'perf-chart-cls',     title: 'CLS',         yTitle: 'value', ySuffix: '',  decimals: 3 },
    { key: 'db_total_ms', elId: 'perf-chart-db',     title: 'DB Total',    yTitle: 'ms', ySuffix: ' ms', decimals: 0 },
    { key: 'bundle_kb',   elId: 'perf-chart-bundle', title: 'Bundle Size', yTitle: 'KB', ySuffix: ' KB', decimals: 1 },
];

// Stable Bootstrap-ish 14-colour palette, deterministic per route (D-06)
// Derived from existing codebase: sv-farmer.ts:51, stardew.ts:462,486
const PALETTE = [
    '#3d9970',  // foraging green
    '#339af0',  // fishing blue
    '#e03131',  // combat red
    '#cc5de8',  // museum purple
    '#fab005',  // mining gold
    '#6aa84f',  // farming green
    '#999999',  // mining gray
    '#ff922b',  // orange
    '#15aabf',  // teal
    '#7048e8',  // indigo
    '#f06595',  // pink
    '#22b8cf',  // cyan
    '#82c91e',  // lime
    '#fd7e14',  // dark orange
];

/**
 * Map a route slug to a stable colour index using a djb2-style hash.
 * Same route → same colour across all 6 charts AND across page reloads (D-06).
 */
export function colorForRoute(route: string): string {
    let h = 0;
    for (let i = 0; i < route.length; i++) {
        h = (h * 31 + route.charCodeAt(i)) | 0;
    }
    return PALETTE[Math.abs(h) % PALETTE.length];
}

// ── DataTables init ─────────────────────────────────────────────────────────

function initTable(): void {
    const el = document.querySelector('.datatable[data-table="benchmarks"]') as HTMLTableElement | null;
    if (!el) return;

    new DataTable(el, {
        pageLength: 25,
        order: [[0, 'desc'], [2, 'asc']],  // Run timestamp DESC, Route ASC
        // searching/paging/info all default true
        // responsive plugin loaded globally via index.ts
    });
}

// ── Highcharts init ─────────────────────────────────────────────────────────

function initCharts(data: PerfChartPayload): void {
    for (const m of METRICS) {
        const el = document.getElementById(m.elId);
        if (!el) continue;

        const seriesByRoute: MetricSeries = data.series[m.key] || {};

        const series = data.routes.map(route => ({
            type: 'line' as const,
            name: route,
            color: colorForRoute(route),
            data: seriesByRoute[route] ?? [],
            marker: { enabled: true, radius: 3 },
        }));

        // Anti-pattern guard: never pass explicit undefined to Highcharts
        // (memory: feedback_highcharts_undefined_options) — use conditional spread.
        Highcharts.chart(el, Highcharts.merge(getChartTheme(), {
            chart: {
                type: 'line',
                height: 320,
            },
            title: {
                text: m.title,
                style: { fontSize: '14px' },
            },
            credits: { enabled: false },
            xAxis: {
                type: 'datetime',
            },
            yAxis: {
                title: { text: m.yTitle },
                gridLineWidth: 0.1,
            },
            tooltip: {
                xDateFormat: '%Y-%m-%d %H:%M',
                pointFormatter: function (this: Highcharts.Point): string {
                    const val = this.y !== null && this.y !== undefined
                        ? Number(this.y).toLocaleString(undefined, { maximumFractionDigits: m.decimals })
                        : '—';
                    return `<span style="color:${(this as any).color}">●</span> ${this.series.name}: <b>${val}${m.ySuffix}</b><br/>`;
                },
            },
            plotOptions: {
                line: {
                    animation: false,
                    marker: { enabled: true, radius: 3 },
                },
            },
            series,
        } as Highcharts.Options));
    }
}

// ── Module export ───────────────────────────────────────────────────────────

export default class {
    static init = (): void => {
        if (!window.perf_meta?.hasData) return;
        if (!window.perf_data) return;
        initTable();
        initCharts(window.perf_data);

        // D-09: smooth-scroll deep-linked run row into view on ?run=<id>
        const runParam = new URLSearchParams(location.search).get('run');
        if (runParam) {
            document.getElementById('run-' + runParam)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };
}
